import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { socSeverityColor, socStatusColor, socStatusLabel, humanTime, SOC_STATUS_OPTIONS, SOC_SEVERITIES, SOC_SOURCES, SOC_CLASSIFICATIONS, priorityColor, tlpColor, TLP_OPTIONS, PAP_OPTIONS, papColor, SOC_IOC_TYPES, SOC_TASK_STATUS_OPTIONS, socAllowedNextStatuses } from "@/lib/utils";
import { ArrowLeft, ShieldWarning, PaperPlaneRight, Lock, Paperclip, DownloadSimple, CheckCircle, ArrowCounterClockwise, Crosshair, Bug, Trash, Plus, MagnifyingGlass, ArrowBendUpRight, ArrowSquareOut, FilePdf, ListChecks, Eye, X, ChatText, HandGrabbing, ArrowUUpLeft, ArrowFatLinesUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import { renderMarkdown } from "@/lib/md";
import { useBreadcrumb } from "@/components/Breadcrumbs";
import { RichText } from "@/components/EmailBody";
import { MitreGateDialog, MitreBadge, mitreState } from "@/components/MitreGate";
import { fmtIST } from "@/lib/utils";

const isLead = (u) => u && (u.role === "soc_lead" || u.role === "admin");
const isAuditor = (u) => u && u.role === "soc_auditor";
const IOC_TYPES = SOC_IOC_TYPES;
const SEV_PRIO = { Critical: "P1", High: "P2", Medium: "P3", Low: "P4" };
const INCIDENT_TYPES = ["Incident", "Service Request", "Security Incident"];

export default function SocCaseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  useBreadcrumb(c?.number);
  const [comments, setComments] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [iocs, setIocs] = useState([]);
  const [mitreAll, setMitreAll] = useState([]);
  const [techSearch, setTechSearch] = useState("");
  const [newIoc, setNewIoc] = useState({ type: "ip", value: "", description: "" });
  const [analysts, setAnalysts] = useState([]);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeForm, setCloseForm] = useState({ close_reason: "", classification: "True Positive" });
  const [checklist, setChecklist] = useState([]);
  const [playbooks, setPlaybooks] = useState([]);
  const [pbSel, setPbSel] = useState("");
  const [intel, setIntel] = useState({});
  const [enriching, setEnriching] = useState(null);
  const [links, setLinks] = useState([]);
  const [escOpen, setEscOpen] = useState(false);
  const [escForm, setEscForm] = useState({ title: "", description: "", priority: "P2", client: "Unassigned", incident_type: "Incident" });
  const [teams, setTeams] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [ttSel, setTtSel] = useState("");
  const [newTask, setNewTask] = useState({ title: "", description: "", assignee_id: "unassigned", status: "todo" });
  const [tagInput, setTagInput] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkPreview, setBulkPreview] = useState([]);
  const [expandedTask, setExpandedTask] = useState(null);
  const [taskComments, setTaskComments] = useState({});
  const [taskCommentInput, setTaskCommentInput] = useState({});
  const [related, setRelated] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [hnote, setHnote] = useState("");

  const load = useCallback(() => {
    api.get(`/soc/cases/${id}`).then(r => setC(r.data)).catch(() => toast.error("Case not found"));
    api.get(`/soc/cases/${id}/comments`).then(r => setComments(r.data)).catch(() => {});
    api.get(`/soc/cases/${id}/evidence`).then(r => setEvidence(r.data)).catch(() => {});
    api.get(`/soc/cases/${id}/iocs`).then(r => setIocs(r.data)).catch(() => {});
    api.get(`/soc/cases/${id}/checklist`).then(r => setChecklist(r.data)).catch(() => {});
    api.get(`/soc/cases/${id}/links`).then(r => setLinks(r.data)).catch(() => {});
    api.get(`/soc/cases/${id}/tasks`).then(r => setTasks(r.data)).catch(() => {});
    api.get(`/soc/cases/${id}/related`).then(r => setRelated(r.data)).catch(() => {});
    api.get(`/soc/cases/${id}/suggested-playbooks`).then(r => setSuggested(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!id) return;
    const beat = () => api.post(`/soc/cases/${id}/presence`).then(r => setViewers(r.data.viewers || [])).catch(() => {});
    beat();
    const t = setInterval(beat, 15000);
    return () => clearInterval(t);
  }, [id]);
  useEffect(() => { if (c) setHnote(c.handover_note || ""); }, [c?.id]);
  useEffect(() => {
    api.get("/soc/mitre").then(r => setMitreAll(r.data)).catch(() => {});
    api.get("/soc/playbooks").then(r => setPlaybooks(r.data)).catch(() => {});
    api.get("/soc/task-templates").then(r => setTaskTemplates(r.data)).catch(() => {});
    api.get("/soc/teams").then(r => setTeams(r.data)).catch(() => {});
    if (user?.role === "soc_auditor") return;
    api.get("/client-registry?module=soc").then(r => setClients(r.data)).catch(() => {});
    api.get("/soc/settings").then(r => setCategories(r.data.categories || [])).catch(() => {});
  }, [user]);
  // Assignee picker: scope to the case team's roster when a team is set, else all analysts.
  useEffect(() => {
    const q = c?.team_id ? `?team_id=${c.team_id}` : "";
    api.get(`/soc/analysts${q}`).then(r => setAnalysts(r.data)).catch(() => {});
  }, [c?.team_id]);

  const noClient = (v) => !v || String(v).trim().toLowerCase() === "unassigned";
  const patch = async (body) => {
    if (!("client" in body) && noClient(c?.client)) {
      toast.error("Select a client before saving changes to this case.");
      document.querySelector('[data-testid="soc-client-select"]')?.focus();
      return;
    }
    try { const { data } = await api.patch(`/soc/cases/${id}`, body); setC(data); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const assign = async (val) => {
    try {
      const { data } = await api.post(`/soc/cases/${id}/assign`, { assignee_id: val === "unassigned" ? null : val });
      if (!isLead(user) && data.assignee_id && data.assignee_id !== user?.id) {
        toast.success(`${data.number} handed to ${data.assignee_name}`);
        nav("/soc/cases");
        return;
      }
      setC(data); load();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
  };
  const takeCase = async () => {
    try { const { data } = await api.post(`/soc/cases/${id}/take`); setC(data); load(); toast.success(`${data.number} is now assigned to you`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not take case"); }
  };
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [releaseReason, setReleaseReason] = useState("");
  const [escT2Open, setEscT2Open] = useState(false);
  const [escT2, setEscT2] = useState({ mode: "analyst", assignee_id: "", team_id: "", note: "" });
  const [allAnalysts, setAllAnalysts] = useState([]);
  useEffect(() => { if (escT2Open) api.get("/soc/analysts").then(r => setAllAnalysts(r.data || [])).catch(() => {}); }, [escT2Open]);
  const tier2Targets = allAnalysts.filter(a => a.id !== user?.id && (a.role === "soc_lead" || (a.soc_tier || 1) >= 2));
  const handoffT2 = async () => {
    const body = { note: escT2.note, assignee_id: escT2.mode === "analyst" ? escT2.assignee_id : "", team_id: escT2.mode === "team" ? escT2.team_id : "" };
    try { const { data } = await api.post(`/soc/cases/${id}/handoff`, body); setC(data); load(); setEscT2Open(false); setEscT2({ mode: "analyst", assignee_id: "", team_id: "", note: "" }); toast.success(`${data.number} escalated to Tier 2`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Escalation failed"); }
  };
  const releaseCase = async () => {
    try { const { data } = await api.post(`/soc/cases/${id}/release`, { reason: releaseReason }); setC(data); load(); setReleaseOpen(false); setReleaseReason(""); toast.success(`${data.number} returned to the unassigned queue`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not release case"); }
  };
  const sendComment = async () => {
    if (!reply.trim()) return;
    try { await api.post(`/soc/cases/${id}/comments`, { body: reply, internal }); setReply(""); load(); }
    catch (e) { toast.error("Could not add note"); }
  };
  const [gate, setGate] = useState({ enabled: true, min_reason_len: 10 });
  const [gateOpen, setGateOpen] = useState(false);
  const [pending, setPending] = useState(null); // { kind: "status", status } | { kind: "close" }
  useEffect(() => { api.get("/soc/settings").then(r => r.data.mitre_gate && setGate(r.data.mitre_gate)).catch(() => {}); }, []);
  const needsGate = (classification) => gate.enabled && classification === "True Positive" && mitreState(c) === "none" && !["resolved", "closed"].includes(c?.status);
  const doClose = async (extra = {}) => {
    const body = { ...closeForm, ...extra };
    if (!Object.keys(extra).length && needsGate(body.classification || c.classification)) { setPending({ kind: "close" }); setGateOpen(true); return; }
    try { await api.post(`/soc/cases/${id}/close`, body); setCloseOpen(false); load(); toast.success("Case closed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Close failed"); throw e; }
  };
  const changeStatus = async (v) => {
    if (["resolved", "closed"].includes(v) && needsGate(c.classification)) { setPending({ kind: "status", status: v }); setGateOpen(true); return; }
    await patch({ status: v });
  };
  const gateProceed = async (extra) => {
    if (pending?.kind === "close") return doClose(extra);
    const { data } = await api.patch(`/soc/cases/${id}`, { status: pending.status, ...extra }).catch(e => { toast.error(e?.response?.data?.detail || "Update failed"); throw e; });
    setC(data); load();
  };
  const reopen = async () => {
    try { await api.post(`/soc/cases/${id}/reopen`); load(); toast.success("Case reopened"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reopen failed"); }
  };
  const uploadEvidence = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try { await api.post(`/soc/cases/${id}/evidence`, { filename: file.name, content_type: file.type, data_url: reader.result }); load(); toast.success("Evidence added"); }
      catch { toast.error("Upload failed"); }
    };
    reader.readAsDataURL(file);
  };
  const download = async (aid) => {
    const { data } = await api.get(`/soc/evidence/${aid}`);
    const a = document.createElement("a"); a.href = data.data_url; a.download = data.filename; a.click();
  };
  const addIoc = async () => {
    if (!newIoc.value.trim()) { toast.error("IOC value required"); return; }
    try { await api.post(`/soc/cases/${id}/iocs`, newIoc); setNewIoc({ type: newIoc.type, value: "", description: "" }); loadIocs(); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add IOC"); }
  };
  const delIoc = async (iid) => { try { await api.delete(`/soc/iocs/${iid}`); loadIocs(); } catch { toast.error("Delete failed"); } };
  const loadIocs = () => api.get(`/soc/cases/${id}/iocs`).then(r => setIocs(r.data)).catch(() => {});
  const toggleTech = async (tid) => {
    const cur = c.mitre_techniques || [];
    const next = cur.includes(tid) ? cur.filter(x => x !== tid) : [...cur, tid];
    await patch({ mitre_techniques: next });
  };
  const applyPb = async () => {
    if (!pbSel) return;
    try { const { data } = await api.post(`/soc/cases/${id}/playbook`, { playbook_id: pbSel }); setChecklist(data); setPbSel(""); load(); toast.success("Playbook applied"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); }
  };
  const applyPbId = async (pid) => {
    try { const { data } = await api.post(`/soc/cases/${id}/playbook`, { playbook_id: pid }); setChecklist(data); load(); toast.success("Playbook applied"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); }
  };
  const toggleStep = async (item) => {
    try { await api.patch(`/soc/checklist/${item.id}`, { done: !item.done }); setChecklist(cl => cl.map(x => x.id === item.id ? { ...x, done: !item.done, done_by: !item.done ? user?.name : null } : x)); }
    catch { toast.error("Update failed"); }
  };
  const delStep = async (iid) => { try { await api.delete(`/soc/checklist/${iid}`); setChecklist(cl => cl.filter(x => x.id !== iid)); } catch { toast.error("Delete failed"); } };
  const enrichIoc = async (iid) => {
    setEnriching(iid);
    try { const { data } = await api.post(`/soc/iocs/${iid}/enrich`); setIntel(m => ({ ...m, [iid]: data })); if (data.error) toast.error(data.error); else toast.success("IOC enriched"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Enrich failed"); }
    finally { setEnriching(null); }
  };
  const openEscalate = () => { setEscForm({ title: c.title || "", description: "", priority: SEV_PRIO[c.severity] || "P2", client: c.client || "Unassigned", incident_type: "Incident" }); setEscOpen(true); };
  const doEscalate = async () => {
    if (!escForm.title.trim()) { toast.error("Title required"); return; }
    try { await api.post(`/soc/cases/${id}/escalate`, escForm); setEscOpen(false); load(); toast.success("Escalated to ITSM"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Escalation failed"); }
  };
  const addTag = async () => {
    const t = tagInput.trim();
    if (!t) return;
    const next = [...new Set([...(c.tags || []), t])];
    setTagInput("");
    await patch({ tags: next });
  };
  const removeTag = async (t) => patch({ tags: (c.tags || []).filter(x => x !== t) });
  const setTeam = async (val) => patch({ team_id: val });
  const exportPdf = async () => {
    try {
      const res = await api.get(`/soc/cases/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${c.number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const updateIoc = async (iid, body) => {
    try { const { data } = await api.patch(`/soc/iocs/${iid}`, body); setIocs(list => list.map(x => x.id === iid ? data : x)); }
    catch { toast.error("Update failed"); }
  };
  const addIocTag = async (io, tag) => {
    const t = (tag || "").trim();
    if (!t) return;
    updateIoc(io.id, { tags: [...new Set([...(io.tags || []), t])] });
  };
  const parseBulk = async () => {
    try { const { data } = await api.post("/soc/iocs/parse", { text: bulkText }); setBulkPreview(data.items || []); if (!(data.items || []).length) toast.error("Nothing recognised"); }
    catch { toast.error("Parse failed"); }
  };
  const commitBulk = async () => {
    try { const { data } = await api.post(`/soc/cases/${id}/iocs/bulk`, { items: bulkPreview }); setIocs(data.iocs); setBulkOpen(false); setBulkText(""); setBulkPreview([]); toast.success(`${data.added} observable(s) added`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Bulk add failed"); }
  };
  const addTask = async () => {
    if (!newTask.title.trim()) { toast.error("Task title required"); return; }
    try { await api.post(`/soc/cases/${id}/tasks`, newTask); setNewTask({ title: "", description: "", assignee_id: "unassigned", status: "todo" }); api.get(`/soc/cases/${id}/tasks`).then(r => setTasks(r.data)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add task"); }
  };
  const updateTask = async (tid, body) => {
    try { const { data } = await api.patch(`/soc/tasks/${tid}`, body); setTasks(ts => ts.map(x => x.id === tid ? data : x)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const delTask = async (tid) => { try { await api.delete(`/soc/tasks/${tid}`); setTasks(ts => ts.filter(x => x.id !== tid)); } catch { toast.error("Delete failed"); } };
  const applyTaskTemplate = async () => {
    if (!ttSel) return;
    try { const { data } = await api.post(`/soc/cases/${id}/apply-task-template`, { template_id: ttSel }); setTasks(data); setTtSel(""); toast.success("Task template applied"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); }
  };
  const loadTaskComments = (tid) => api.get(`/soc/tasks/${tid}/comments`).then(r => setTaskComments(m => ({ ...m, [tid]: r.data }))).catch(() => {});
  const toggleTask = (tid) => { const nx = expandedTask === tid ? null : tid; setExpandedTask(nx); if (nx) loadTaskComments(nx); };
  const addTaskComment = async (tid) => {
    const body = (taskCommentInput[tid] || "").trim();
    if (!body) return;
    try { await api.post(`/soc/tasks/${tid}/comments`, { body }); setTaskCommentInput(m => ({ ...m, [tid]: "" })); loadTaskComments(tid); }
    catch { toast.error("Could not add comment"); }
  };

  if (!c) return <div className="p-6 text-muted-foreground">Loading case…</div>;
  const closed = c.status === "closed";
  const mine = c.assignee_id && c.assignee_id === user?.id;
  const auditor = isAuditor(user);
  const canClose = !auditor && (isLead(user) || mine);
  const canEdit = !auditor && (isLead(user) || !c.assignee_id || mine);
  const statusOptions = socAllowedNextStatuses(c.status);
  const doneCount = checklist.filter(x => x.done).length;
  const pbProgress = checklist.length ? Math.round((doneCount / checklist.length) * 100) : 0;
  const techName = (tid) => mitreAll.find(t => t.id === tid)?.name || tid;
  const techMatches = techSearch.trim()
    ? mitreAll.filter(t => `${t.id} ${t.name}`.toLowerCase().includes(techSearch.toLowerCase())).slice(0, 40)
    : [];

  return (
    <div className="p-6" data-testid="soc-case-detail-view">
      <Link to="/soc/cases" data-testid="soc-back" className="text-xs font-mono text-muted-foreground hover:text-primary flex items-center gap-1 mb-3"><ArrowLeft size={14} /> Back to cases</Link>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span className="font-mono text-sm text-primary">{c.number}</span>
            <span className={`text-[10px] font-mono px-2 py-0.5 border ${socSeverityColor(c.severity)}`}>{c.severity}</span>
            <span className={`text-[10px] font-mono px-2 py-0.5 border ${priorityColor(c.priority)}`}>{c.priority}</span>
            <span className={`text-[10px] font-mono px-2 py-0.5 border ${socStatusColor(c.status)}`}>{socStatusLabel(c.status)}</span>
            <span data-testid="soc-tlp-badge" className={`text-[10px] font-mono px-2 py-0.5 border ${tlpColor(c.tlp)}`}>TLP:{c.tlp}</span>
            <MitreBadge c={c} lead={isLead(user)} />
            {c.resolution_breached && <span data-testid="soc-sla-breach" className="text-[10px] font-mono px-2 py-0.5 border border-rag-red/40 text-rag-red-fg">SLA BREACH</span>}
            {!c.resolution_breached && c.sla_due_soon && <span data-testid="soc-sla-due-soon" className="text-[10px] font-mono px-2 py-0.5 border border-rag-amber/50 text-rag-amber-fg">SLA DUE SOON</span>}
            {c.possible_duplicate_number && <span data-testid="soc-dup-badge" className="text-[10px] font-mono px-2 py-0.5 border border-rag-amber/50 text-rag-amber-fg">POSSIBLE DUPLICATE OF {c.possible_duplicate_number}</span>}
            {c.possibly_related_number && <span data-testid="soc-related-badge" className="text-[10px] font-mono px-2 py-0.5 border border-border text-muted-foreground">POSSIBLY RELATED TO {c.possibly_related_number}</span>}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2"><ShieldWarning size={24} /> {c.title}</h1>
          <div className="text-xs text-muted-foreground font-mono mt-1">{c.client} · {c.source} · reported {c.reporter || "—"} · <span title={fmtIST(c.created_at)} data-testid="soc-case-created-ist">{fmtIST(c.created_at)}</span></div>
          {viewers.length > 0 && (
            <div data-testid="soc-presence" className="flex items-center gap-1.5 mt-1.5">
              {viewers.map(v => (
                <span key={v.user_id} title={`${v.name} is viewing`} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-rag-green-bg text-rag-green-fg border border-rag-green/30">● {v.name}</span>
              ))}
              <span className="text-[10px] text-muted-foreground">also viewing</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="soc-export-btn" onClick={exportPdf} variant="outline" className="rounded-sm font-mono uppercase text-xs"><FilePdf size={14} className="mr-1" /> Export Case</Button>
          {canEdit && !closed && <Button data-testid="soc-escalate-btn" onClick={openEscalate} variant="outline" className="rounded-sm font-mono uppercase text-xs"><ArrowBendUpRight size={14} className="mr-1" /> Escalate to ITSM</Button>}
          {canClose && !closed && <Button data-testid="soc-close-btn" onClick={() => setCloseOpen(true)} variant="outline" className="rounded-sm font-mono uppercase text-xs"><CheckCircle size={14} className="mr-1" /> Close</Button>}
          {canClose && closed && <Button data-testid="soc-reopen-btn" onClick={reopen} variant="outline" className="rounded-sm font-mono uppercase text-xs"><ArrowCounterClockwise size={14} className="mr-1" /> Reopen</Button>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <Card className="p-5 border-border rounded-sm">
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Description</div>
            <RichText text={c.description || "—"} className="text-sm leading-relaxed" />
          </Card>

          <Tabs defaultValue="timeline">
            <TabsList className="rounded-sm">
              <TabsTrigger value="timeline" data-testid="soc-tab-timeline">Timeline</TabsTrigger>
              <TabsTrigger value="evidence" data-testid="soc-tab-evidence">Evidence ({evidence.length})</TabsTrigger>
              <TabsTrigger value="iocs" data-testid="soc-tab-iocs">IOCs ({iocs.length})</TabsTrigger>
              <TabsTrigger value="tasks" data-testid="soc-tab-tasks">Tasks ({tasks.length})</TabsTrigger>
              <TabsTrigger value="playbook" data-testid="soc-tab-playbook">Playbook ({doneCount}/{checklist.length})</TabsTrigger>
              <TabsTrigger value="attack" data-testid="soc-tab-attack">ATT&CK ({(c.mitre_techniques || []).length})</TabsTrigger>
            </TabsList>
            <TabsContent value="timeline" className="space-y-3 mt-3">
              {canEdit && (
                <Card className="p-4 border-border rounded-sm">
                  <Textarea data-testid="soc-comment-input" value={reply} onChange={e => setReply(e.target.value)} placeholder="Add an investigation note… (use @name to mention)" className="rounded-sm min-h-[70px] text-sm" />
                  <div className="flex items-center justify-between mt-2">
                    <label className="flex items-center gap-2 text-xs font-mono text-muted-foreground cursor-pointer">
                      <input type="checkbox" data-testid="soc-comment-internal" checked={internal} onChange={e => setInternal(e.target.checked)} /> <Lock size={12} /> Internal
                    </label>
                    <Button data-testid="soc-comment-submit" onClick={sendComment} size="sm" className="rounded-sm font-mono uppercase text-xs"><PaperPlaneRight size={13} className="mr-1" /> Add note</Button>
                  </div>
                </Card>
              )}
              {comments.map(cm => (
                <div key={cm.id} data-testid={`soc-comment-${cm.id}`} className={`p-3 rounded-sm border text-sm ${cm.internal ? "border-rag-amber/30 bg-rag-amber-bg" : "border-border bg-card/40"}`}>
                  <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground mb-1">
                    <span className="text-foreground">{cm.author_name}</span>{cm.internal && <Lock size={11} className="text-rag-amber-fg" />}<span className="ml-auto" title={fmtIST(cm.created_at, { seconds: true })}>{fmtIST(cm.created_at)}</span>
                  </div>
                  {cm.source === "email"
                    ? <RichText text={cm.body} className="text-sm leading-relaxed" />
                    : <div className="soc-md text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: renderMarkdown(cm.body) }} />}
                </div>
              ))}
              {comments.length === 0 && <div className="text-xs text-muted-foreground">No activity yet.</div>}
            </TabsContent>
            <TabsContent value="evidence" className="space-y-2 mt-3">
              <Card className="p-4 border-border rounded-sm">
                <label className="flex items-center gap-2 text-sm cursor-pointer text-primary hover:underline w-fit">
                  <Paperclip size={15} /> Add evidence file (≤5MB)
                  <input type="file" data-testid="soc-evidence-upload" onChange={uploadEvidence} className="hidden" />
                </label>
              </Card>
              {evidence.map(a => (
                <div key={a.id} data-testid={`soc-evidence-${a.id}`} className="flex items-center gap-2 text-sm border border-border rounded-sm px-3 py-2 bg-card/40">
                  <Paperclip size={14} className="text-muted-foreground" />
                  <span className="truncate flex-1">{a.filename}</span>
                  <span className="text-xs text-muted-foreground font-mono">{a.uploaded_by}</span>
                  <button data-testid={`soc-evidence-dl-${a.id}`} onClick={() => download(a.id)} className="text-muted-foreground hover:text-primary"><DownloadSimple size={15} /></button>
                </div>
              ))}
              {evidence.length === 0 && <div className="text-xs text-muted-foreground">No evidence attached.</div>}
            </TabsContent>
            <TabsContent value="iocs" className="space-y-2 mt-3">
              {related.length > 0 && (
                <Card className="p-4 border-border rounded-sm border-rag-amber/30 bg-rag-amber-bg" data-testid="soc-related-cases">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-rag-amber-fg mb-2 flex items-center gap-2">
                    <ArrowSquareOut size={13} /> Related cases ({related.length}) — sharing observables
                  </div>
                  <div className="space-y-1.5">
                    {related.map(rc => (
                      <button key={rc.id} data-testid={`soc-related-${rc.number}`} onClick={() => nav(`/soc/cases/${rc.id}`)}
                        className="w-full text-left flex items-center gap-2 text-xs px-2 py-1.5 rounded-sm border border-border/60 bg-background/40 hover:bg-accent/30 transition-colors">
                        <span className="font-mono text-primary">{rc.number}</span>
                        <span className="truncate flex-1">{rc.title}</span>
                        <span className={`text-[9px] font-mono px-1.5 py-0.5 border ${socStatusColor(rc.status)}`}>{socStatusLabel(rc.status)}</span>
                        <span className="text-[10px] font-mono text-rag-amber-fg shrink-0" title={rc.shared_observables.map(s => s.value).join(", ")}>
                          {rc.shared_count} shared
                        </span>
                      </button>
                    ))}
                  </div>
                </Card>
              )}
              <Card className="p-4 border-border rounded-sm">
                <div className="flex flex-wrap items-end gap-2">
                  <Select value={newIoc.type} onValueChange={v => setNewIoc({ ...newIoc, type: v })}>
                    <SelectTrigger data-testid="soc-ioc-type" className="rounded-sm h-9 w-28 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{IOC_TYPES.map(t => <SelectItem key={t} value={t}>{t.toUpperCase()}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input data-testid="soc-ioc-value" value={newIoc.value} onChange={e => setNewIoc({ ...newIoc, value: e.target.value })} placeholder="Indicator value (IP, domain, hash…)" className="rounded-sm h-9 text-sm flex-1 min-w-[180px] font-mono" />
                  <Input data-testid="soc-ioc-desc" value={newIoc.description} onChange={e => setNewIoc({ ...newIoc, description: e.target.value })} placeholder="Note (optional)" className="rounded-sm h-9 text-sm w-40" />
                  <Button data-testid="soc-ioc-add" onClick={addIoc} disabled={!canEdit} size="sm" className="rounded-sm font-mono uppercase text-xs h-9"><Plus size={14} className="mr-1" /> Add</Button>
                  <Button data-testid="soc-ioc-bulk-open" onClick={() => setBulkOpen(true)} disabled={!canEdit} size="sm" variant="outline" className="rounded-sm font-mono uppercase text-xs h-9">Bulk add</Button>
                </div>
              </Card>
              {iocs.map(io => {
                const enr = intel[io.id] || io.intel;
                const canEnrich = ["ip", "domain", "url", "hash"].includes(io.type);
                return (
                  <div key={io.id} data-testid={`soc-ioc-${io.id}`} className="border border-border rounded-sm bg-card/40">
                    <div className="flex items-center gap-2 text-sm px-3 py-2">
                      <span className="text-[10px] font-mono uppercase px-2 py-0.5 border border-border rounded-sm">{io.type}</span>
                      <span className={`font-mono truncate flex-1 ${io.is_ioc === false ? "text-muted-foreground" : ""}`}>{io.value}</span>
                      {io.seen_in_other_cases > 0 && <span data-testid={`soc-ioc-seen-${io.id}`} className="text-[9px] font-mono px-1.5 py-0.5 border border-rag-amber/50 text-rag-amber-fg rounded-sm shrink-0" title={`Recurrence: seen in ${io.times_seen || io.seen_in_other_cases + 1} case(s)${io.first_seen ? ` · first ${humanTime(io.first_seen)}` : ""}${io.last_seen ? ` · last ${humanTime(io.last_seen)}` : ""}`}>seen in {io.seen_in_other_cases} other{io.seen_in_other_cases === 1 ? " case" : " cases"}</span>}
                      {io.library_match && <span data-testid={`soc-ioc-libmatch-${io.id}`} className="text-[9px] font-mono px-1.5 py-0.5 border border-rag-red/60 bg-rag-red-bg text-rag-red-fg rounded-sm shrink-0 flex items-center gap-1" title={`Threat Intel Library hit — feeds: ${(io.library_match.source_feeds || []).join(", ")}${io.library_match.malware ? ` · ${io.library_match.malware}` : ""} · confidence ${io.library_match.confidence}%`}><ShieldWarning size={10} /> known bad · {io.library_match.confidence}%</span>}
                      {io.description && <span className="text-xs text-muted-foreground truncate max-w-[140px]">{io.description}</span>}
                      {canEnrich && (
                        <button data-testid={`soc-ioc-enrich-${io.id}`} onClick={() => enrichIoc(io.id)} disabled={enriching === io.id}
                          className="text-xs font-mono uppercase text-primary hover:underline disabled:opacity-50 flex items-center gap-1">
                          <MagnifyingGlass size={13} /> {enriching === io.id ? "…" : "Enrich"}
                        </button>
                      )}
                      {canEdit && <button data-testid={`soc-ioc-del-${io.id}`} onClick={() => delIoc(io.id)} className="text-muted-foreground hover:text-rag-red"><Trash size={14} /></button>}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 px-3 pb-2 text-[10px] font-mono">
                      <button data-testid={`soc-ioc-isioc-${io.id}`} disabled={!canEdit} onClick={() => updateIoc(io.id, { is_ioc: !(io.is_ioc !== false) })}
                        className={`px-2 py-0.5 border rounded-sm ${io.is_ioc !== false ? "border-rag-red/40 text-rag-red-fg" : "border-border text-muted-foreground"}`}>
                        {io.is_ioc !== false ? "IOC" : "artifact"}
                      </button>
                      <button data-testid={`soc-ioc-sighted-${io.id}`} disabled={!canEdit} onClick={() => updateIoc(io.id, { sighted: !io.sighted })}
                        className={`px-2 py-0.5 border rounded-sm flex items-center gap-1 ${io.sighted ? "border-rag-amber/50 text-rag-amber-fg" : "border-border text-muted-foreground"}`}>
                        <Eye size={11} /> {io.sighted ? "sighted" : "not sighted"}
                      </button>
                      <span className="flex items-center gap-1">TLP
                        <Select value={io.tlp || "AMBER"} onValueChange={v => updateIoc(io.id, { tlp: v })} disabled={!canEdit}>
                          <SelectTrigger data-testid={`soc-ioc-tlp-${io.id}`} className={`h-6 rounded-sm text-[10px] px-1.5 border ${tlpColor(io.tlp)}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{TLP_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                        </Select>
                      </span>
                      <span className="flex items-center gap-1">PAP
                        <Select value={io.pap || "AMBER"} onValueChange={v => updateIoc(io.id, { pap: v })} disabled={!canEdit}>
                          <SelectTrigger data-testid={`soc-ioc-pap-${io.id}`} className={`h-6 rounded-sm text-[10px] px-1.5 border ${papColor(io.pap)}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{PAP_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                        </Select>
                      </span>
                      {(io.tags || []).map(t => (
                        <span key={t} className="flex items-center gap-1 px-1.5 py-0.5 border border-border rounded-sm">
                          {t}{canEdit && <button onClick={() => updateIoc(io.id, { tags: (io.tags || []).filter(x => x !== t) })} className="hover:text-rag-red"><X size={10} /></button>}
                        </span>
                      ))}
                      {canEdit && (
                        <input data-testid={`soc-ioc-tag-input-${io.id}`} placeholder="+tag" onKeyDown={e => { if (e.key === "Enter") { addIocTag(io, e.target.value); e.target.value = ""; } }}
                          className="w-16 bg-transparent border border-dashed border-border rounded-sm px-1.5 py-0.5 outline-none focus:border-primary" />
                      )}
                    </div>
                    {enr && !enr.error && (
                      <div data-testid={`soc-ioc-intel-${io.id}`} className="flex flex-wrap gap-2 px-3 pb-2 text-[10px] font-mono">
                        {enr.virustotal?.status === "ok" && (
                          <span className={`px-2 py-0.5 border rounded-sm ${enr.virustotal.malicious > 0 ? "border-rag-red/40 text-rag-red-fg" : "border-rag-green/40 text-rag-green-fg"}`}>
                            VT {enr.virustotal.malicious}/{(enr.virustotal.malicious + enr.virustotal.suspicious + enr.virustotal.harmless + enr.virustotal.undetected) || 0} malicious
                          </span>
                        )}
                        {enr.virustotal && enr.virustotal.status !== "ok" && <span className="px-2 py-0.5 border border-border rounded-sm text-muted-foreground">VT: {enr.virustotal.status}</span>}
                        {enr.abuseipdb?.status === "ok" && (
                          <span className={`px-2 py-0.5 border rounded-sm ${enr.abuseipdb.abuse_confidence_score >= 50 ? "border-rag-red/40 text-rag-red-fg" : enr.abuseipdb.abuse_confidence_score > 0 ? "border-rag-amber/40 text-rag-amber-fg" : "border-rag-green/40 text-rag-green-fg"}`}>
                            AbuseIPDB {enr.abuseipdb.abuse_confidence_score}% {enr.abuseipdb.country_code ? `· ${enr.abuseipdb.country_code}` : ""}
                          </span>
                        )}
                        {enr.abuseipdb && !["ok", "not_applicable"].includes(enr.abuseipdb.status) && <span className="px-2 py-0.5 border border-border rounded-sm text-muted-foreground">AbuseIPDB: {enr.abuseipdb.status}</span>}
                        {enr.cached && <span className="text-muted-foreground">· cached</span>}
                      </div>
                    )}
                    {enr?.error && <div className="px-3 pb-2 text-[10px] font-mono text-rag-amber-fg">{enr.error}</div>}
                  </div>
                );
              })}
              {iocs.length === 0 && <div className="text-xs text-muted-foreground">No indicators of compromise recorded.</div>}
            </TabsContent>
            <TabsContent value="tasks" className="space-y-2 mt-3">
              {canEdit && taskTemplates.length > 0 && (
                <Card className="p-3 border-border rounded-sm border-primary/30 bg-primary/5 flex flex-wrap items-center gap-2" data-testid="soc-apply-template-bar">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-primary">Apply task template</span>
                  <Select value={ttSel} onValueChange={setTtSel}>
                    <SelectTrigger data-testid="soc-tasktpl-select" className="rounded-sm h-8 text-xs w-64 ml-auto"><SelectValue placeholder="Choose a template…" /></SelectTrigger>
                    <SelectContent>{taskTemplates.filter(t => t.enabled).map(t => <SelectItem key={t.id} value={t.id}>{t.name} · {t.case_type} ({t.task_items?.length || 0})</SelectItem>)}</SelectContent>
                  </Select>
                  <Button data-testid="soc-tasktpl-apply" onClick={applyTaskTemplate} disabled={!ttSel} size="sm" className="rounded-sm font-mono uppercase text-xs h-8"><Plus size={13} className="mr-1" /> Apply</Button>
                </Card>
              )}
              {canEdit && (
                <Card className="p-4 border-border rounded-sm space-y-2" data-testid="soc-task-form">
                  <Input data-testid="soc-task-title" value={newTask.title} onChange={e => setNewTask({ ...newTask, title: e.target.value })} placeholder="Task title…" className="rounded-sm h-9 text-sm" />
                  <Textarea data-testid="soc-task-desc" value={newTask.description} onChange={e => setNewTask({ ...newTask, description: e.target.value })} placeholder="Description (optional)…" className="rounded-sm text-sm min-h-[54px]" />
                  <div className="flex flex-wrap items-end gap-2">
                    <Select value={newTask.assignee_id} onValueChange={v => setNewTask({ ...newTask, assignee_id: v })}>
                      <SelectTrigger data-testid="soc-task-assignee" className="rounded-sm h-9 w-44 text-xs"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{analysts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={newTask.status} onValueChange={v => setNewTask({ ...newTask, status: v })}>
                      <SelectTrigger data-testid="soc-task-status" className="rounded-sm h-9 w-36 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{SOC_TASK_STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button data-testid="soc-task-add" onClick={addTask} size="sm" className="rounded-sm font-mono uppercase text-xs h-9 ml-auto"><Plus size={14} className="mr-1" /> Add task</Button>
                  </div>
                </Card>
              )}
              {tasks.map(t => (
                <div key={t.id} data-testid={`soc-task-${t.id}`} className="border border-border rounded-sm bg-card/40">
                  <div className="flex items-center gap-2 text-sm px-3 py-2">
                    <span className={`text-[9px] font-mono uppercase px-2 py-0.5 border rounded-sm ${t.status === "done" ? "border-rag-green/40 text-rag-green-fg" : t.status === "in_progress" ? "border-rag-amber/40 text-rag-amber-fg" : "border-border text-muted-foreground"}`}>{(SOC_TASK_STATUS_OPTIONS.find(o => o.value === t.status) || {}).label}</span>
                    <span className={`flex-1 ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>{t.title}</span>
                    <span className="text-[10px] font-mono text-muted-foreground">{t.assignee_name || "unassigned"}</span>
                    <button data-testid={`soc-task-comments-${t.id}`} onClick={() => toggleTask(t.id)} className="text-muted-foreground hover:text-primary"><ChatText size={14} /></button>
                    {canEdit && <button data-testid={`soc-task-del-${t.id}`} onClick={() => delTask(t.id)} className="text-muted-foreground hover:text-rag-red"><Trash size={13} /></button>}
                  </div>
                  {t.description && <div className="px-3 pb-2 text-xs text-muted-foreground whitespace-pre-wrap">{t.description}</div>}
                  {canEdit && (
                    <div className="flex flex-wrap items-center gap-2 px-3 pb-2">
                      <Select value={t.status} onValueChange={v => updateTask(t.id, { status: v })}>
                        <SelectTrigger data-testid={`soc-task-status-${t.id}`} className="rounded-sm h-7 w-32 text-[11px]"><SelectValue /></SelectTrigger>
                        <SelectContent>{SOC_TASK_STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={t.assignee_id || "unassigned"} onValueChange={v => updateTask(t.id, { assignee_id: v })}>
                        <SelectTrigger data-testid={`soc-task-assignee-${t.id}`} className="rounded-sm h-7 w-40 text-[11px]"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                        <SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{analysts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  {expandedTask === t.id && (
                    <div className="px-3 pb-3 border-t border-border/60 pt-2 space-y-2" data-testid={`soc-task-comment-box-${t.id}`}>
                      {(taskComments[t.id] || []).map(cm => (
                        <div key={cm.id} className="text-xs border border-border/60 rounded-sm px-2 py-1.5 bg-background/40">
                          <div className="font-mono text-[10px] text-muted-foreground">{cm.author_name} · {fmtIST(cm.created_at)}</div>
                          <div className="soc-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(cm.body) }} />
                        </div>
                      ))}
                      {(taskComments[t.id] || []).length === 0 && <div className="text-[11px] text-muted-foreground">No comments yet.</div>}
                      {canEdit && (
                        <div className="flex gap-1.5">
                          <Input data-testid={`soc-task-comment-input-${t.id}`} value={taskCommentInput[t.id] || ""} onChange={e => setTaskCommentInput(m => ({ ...m, [t.id]: e.target.value }))} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTaskComment(t.id); } }} placeholder="Comment… (use @name to mention)" className="rounded-sm h-8 text-xs" />
                          <Button data-testid={`soc-task-comment-send-${t.id}`} onClick={() => addTaskComment(t.id)} size="sm" variant="outline" className="rounded-sm h-8"><PaperPlaneRight size={13} /></Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {tasks.length === 0 && <div className="text-xs text-muted-foreground">No tasks yet.</div>}
            </TabsContent>
            <TabsContent value="playbook" className="space-y-3 mt-3">
              <Card className="p-4 border-border rounded-sm">
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-[10px] font-mono uppercase text-muted-foreground">Apply a response playbook</label>
                    <Select value={pbSel} onValueChange={setPbSel}>
                      <SelectTrigger data-testid="soc-playbook-select" className="rounded-sm h-9 text-sm mt-1"><SelectValue placeholder="Choose a playbook…" /></SelectTrigger>
                      <SelectContent>{playbooks.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.steps?.length || 0})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Button data-testid="soc-playbook-apply" onClick={applyPb} disabled={!pbSel || !canEdit} size="sm" className="rounded-sm font-mono uppercase text-xs h-9"><Plus size={14} className="mr-1" /> Apply</Button>
                </div>
              </Card>
              {suggested.length > 0 && (
                <Card className="p-4 border-border rounded-sm border-primary/30 bg-primary/5" data-testid="soc-suggested-playbooks">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-2">Suggested playbooks</div>
                  <div className="space-y-1.5">
                    {suggested.map(s => (
                      <div key={s.id} data-testid={`soc-suggested-${s.id}`} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-sm border border-border/60 bg-background/40">
                        <span className="flex-1 truncate">{s.name} <span className="text-[10px] font-mono text-muted-foreground">({s.step_count})</span></span>
                        <span className={`text-[9px] font-mono px-1.5 py-0.5 border rounded-sm ${s.match === "technique" ? "border-rag-red/40 text-rag-red-fg" : "border-border text-muted-foreground"}`}>
                          {s.match === "technique" ? `ATT&CK: ${s.matched_techniques.join(", ")}` : "category"}
                        </span>
                        {canEdit && <button data-testid={`soc-suggested-apply-${s.id}`} onClick={() => applyPbId(s.id)} className="text-[10px] font-mono uppercase text-primary hover:underline">Apply</button>}
                      </div>
                    ))}
                  </div>
                </Card>
              )}
              {checklist.length > 0 && (
                <Card className="p-4 border-border rounded-sm space-y-3" data-testid="soc-checklist">
                  <div className="flex items-center gap-3">
                    <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Response checklist</div>
                    <div className="ml-auto text-xs font-mono">{doneCount}/{checklist.length} · {pbProgress}%</div>
                  </div>
                  <div className="h-1.5 w-full bg-accent/40 rounded-full overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${pbProgress}%` }} data-testid="soc-checklist-progress" />
                  </div>
                  <div className="space-y-1.5">
                    {checklist.map(item => (
                      <div key={item.id} data-testid={`soc-checklist-item-${item.id}`} className="flex items-start gap-2 text-sm border border-border rounded-sm px-3 py-2 bg-card/40">
                        <input type="checkbox" data-testid={`soc-checklist-toggle-${item.id}`} checked={!!item.done} disabled={!canEdit} onChange={() => toggleStep(item)} className="mt-1 cursor-pointer" />
                        <div className="flex-1 min-w-0">
                          <div className={item.done ? "line-through text-muted-foreground" : ""}>{item.step}</div>
                          <div className="text-[10px] font-mono text-muted-foreground">{item.playbook_name}{item.done && item.done_by ? ` · done by ${item.done_by}` : ""}</div>
                        </div>
                        <button data-testid={`soc-checklist-del-${item.id}`} onClick={() => delStep(item.id)} className="text-muted-foreground hover:text-rag-red"><Trash size={13} /></button>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
              {checklist.length === 0 && <div className="text-xs text-muted-foreground">No playbook applied. Choose one above to load triage steps.</div>}
            </TabsContent>
            <TabsContent value="attack" className="space-y-2 mt-3">
              <Card className="p-4 border-border rounded-sm">
                <div className="relative">
                  <Crosshair size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
                  <Input data-testid="soc-tech-search" value={techSearch} onChange={e => setTechSearch(e.target.value)} placeholder="Search ATT&CK techniques (e.g. T1566 or phishing)…" className="rounded-sm h-9 pl-8 text-sm" />
                </div>
                {techMatches.length > 0 && (
                  <div className="mt-2 max-h-52 overflow-y-auto border border-border rounded-sm divide-y divide-border">
                    {techMatches.map(t => (
                      <button key={t.id} data-testid={`soc-tech-opt-${t.id}`} onClick={() => toggleTech(t.id)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-accent/40 flex items-center gap-2">
                        <span className="font-mono text-xs text-primary w-24 shrink-0">{t.id}</span>
                        <span className="flex-1">{t.name}{t.sub && <span className="text-muted-foreground"> ·sub</span>}</span>
                        {(c.mitre_techniques || []).includes(t.id) && <CheckCircle size={14} className="text-rag-green" />}
                      </button>
                    ))}
                  </div>
                )}
              </Card>
              <div className="flex flex-wrap gap-2">
                {(c.mitre_techniques || []).map(tid => (
                  <span key={tid} data-testid={`soc-tech-${tid}`} className="flex items-center gap-1 text-xs font-mono px-2 py-1 border border-rag-red/30 bg-rag-red-bg rounded-sm">
                    <Bug size={12} /><span className="text-primary">{tid}</span> {techName(tid)}
                    <button onClick={() => toggleTech(tid)} className="text-muted-foreground hover:text-rag-red ml-1"><Trash size={11} /></button>
                  </span>
                ))}
                {(c.mitre_techniques || []).length === 0 && <div className="text-xs text-muted-foreground">No ATT&CK techniques mapped.</div>}
              </div>
              <MitreTacticsCard c={c} patch={patch} canEdit={canEdit} />
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-4">
          <Card className="p-5 border-border rounded-sm space-y-3" data-testid="soc-attributes">
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Attributes</div>
            <Field label="Status">
              <Select value={c.status} onValueChange={changeStatus} disabled={!canEdit}>
                <SelectTrigger data-testid="soc-status-select" className="rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{statusOptions.map(s => <SelectItem key={s} value={s}>{socStatusLabel(s)}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Severity">
              <Select value={c.severity} onValueChange={v => patch({ severity: v })}>
                <SelectTrigger data-testid="soc-severity-select" className="rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{SOC_SEVERITIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Classification">
              <Select value={c.classification} onValueChange={v => patch({ classification: v })}>
                <SelectTrigger data-testid="soc-classification-select" className="rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{SOC_CLASSIFICATIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="TLP">
              <Select value={c.tlp} onValueChange={v => patch({ tlp: v })}>
                <SelectTrigger data-testid="soc-tlp-select" className="rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{TLP_OPTIONS.map(s => <SelectItem key={s} value={s}>TLP:{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Category">
              <Select value={c.category} onValueChange={v => patch({ category: v })}>
                <SelectTrigger data-testid="soc-category-select" className="rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{[...new Set([...(categories || []), c.category].filter(Boolean))].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Source">
              <Select value={c.source} onValueChange={v => patch({ source: v })}>
                <SelectTrigger data-testid="soc-source-select" className="rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{SOC_SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={<span>Client <span className="text-rag-red-fg">*</span></span>}>
              <Select value={c.client} onValueChange={v => patch({ client: v })}>
                <SelectTrigger data-testid="soc-client-select" aria-invalid={noClient(c.client)} className={`rounded-sm h-8 text-xs ${noClient(c.client) ? "border-rag-red-fg text-rag-red-fg" : ""}`}><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent><SelectItem value="Unassigned">Unassigned</SelectItem>{clients.map(cl => <SelectItem key={cl.id} value={cl.name}>{cl.name}</SelectItem>)}</SelectContent>
              </Select>
              {noClient(c.client) && <div className="text-[10px] font-mono text-rag-red-fg mt-1" data-testid="soc-client-required">Required before any other change can be saved</div>}
            </Field>
            <Field label="Assignee">
              <Select value={c.assignee_id || "unassigned"} onValueChange={assign} disabled={auditor || closed || !(isLead(user) || !c.assignee_id || mine)}>
                <SelectTrigger data-testid="soc-assignee-select" className="rounded-sm h-8 text-xs"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{analysts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}{a.id === user?.id ? " (me)" : ""}</SelectItem>)}</SelectContent>
              </Select>
              {!isLead(user) && !auditor && !closed && c.assignee_id && !mine && <div className="text-[10px] font-mono text-muted-foreground mt-1" data-testid="soc-assignee-locked">Owned by {c.assignee_name} — only a lead can reassign</div>}
              {!auditor && !closed && !c.assignee_id && (
                <Button data-testid="soc-take-case" onClick={takeCase} size="sm" className="mt-2 w-full rounded-sm font-mono text-[11px] uppercase tracking-widest h-8">
                  <HandGrabbing size={14} className="mr-1" /> Take case
                </Button>
              )}
              {!auditor && !closed && c.assignee_id && (mine || isLead(user)) && (
                <Button data-testid="soc-release-case" onClick={() => setReleaseOpen(true)} size="sm" variant="outline" className="mt-2 w-full rounded-sm font-mono text-[11px] uppercase tracking-widest h-8">
                  <ArrowUUpLeft size={14} className="mr-1" /> Release to queue
                </Button>
              )}
              {!auditor && !closed && (isLead(user) || !c.assignee_id || mine) && (
                <Button data-testid="soc-escalate-t2" onClick={() => setEscT2Open(true)} size="sm" variant="outline" className="mt-2 w-full rounded-sm font-mono text-[11px] uppercase tracking-widest h-8 border-rag-amber-fg/50 text-rag-amber-fg hover:bg-rag-amber-bg/40">
                  <ArrowFatLinesUp size={14} className="mr-1" /> Escalate to Tier 2
                </Button>
              )}
              {c.escalated_tier === 2 && (
                <div data-testid="soc-escalated-badge" className="mt-2 text-[10px] font-mono uppercase tracking-wider text-rag-amber-fg">Tier 2 · escalated by {c.escalated_by} {c.escalated_at ? humanTime(c.escalated_at) : ""}</div>
              )}
              <Dialog open={escT2Open} onOpenChange={setEscT2Open}>
                <DialogContent className="rounded-sm" data-testid="soc-escalate-t2-dialog">
                  <DialogHeader><DialogTitle className="font-display">Escalate {c.number} to Tier 2</DialogTitle></DialogHeader>
                  <div className="flex items-center gap-1 border border-border rounded-sm p-0.5 w-fit">
                    {[["analyst", "Tier 2 analyst"], ["team", "Team queue"]].map(([m, l]) => (
                      <button key={m} type="button" data-testid={`soc-esc-mode-${m}`} onClick={() => setEscT2({ ...escT2, mode: m })}
                        className={`px-2.5 h-7 text-[11px] font-mono uppercase tracking-wider rounded-sm transition-colors ${escT2.mode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent/40"}`}>{l}</button>
                    ))}
                  </div>
                  {escT2.mode === "analyst" ? (
                    <Select value={escT2.assignee_id} onValueChange={v => setEscT2({ ...escT2, assignee_id: v })}>
                      <SelectTrigger data-testid="soc-esc-analyst" className="rounded-sm h-9 text-sm"><SelectValue placeholder="Choose a Tier 2+ analyst or lead" /></SelectTrigger>
                      <SelectContent>
                        {tier2Targets.map(a => <SelectItem key={a.id} value={a.id}>{a.name} · {a.role === "soc_lead" ? "Lead" : `Tier ${a.soc_tier}`}</SelectItem>)}
                        {tier2Targets.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">No Tier 2 analysts configured</div>}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Select value={escT2.team_id} onValueChange={v => setEscT2({ ...escT2, team_id: v })}>
                      <SelectTrigger data-testid="soc-esc-team" className="rounded-sm h-9 text-sm"><SelectValue placeholder="Choose a team" /></SelectTrigger>
                      <SelectContent>{teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  <Textarea data-testid="soc-esc-note" value={escT2.note} onChange={e => setEscT2({ ...escT2, note: e.target.value })} placeholder="Hand-off note (required): what you checked, indicators found, what Tier 2 should do next…" className="rounded-sm text-sm min-h-[90px]" />
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setEscT2Open(false)} className="rounded-sm font-mono text-xs uppercase">Cancel</Button>
                    <Button data-testid="soc-esc-confirm" onClick={handoffT2} disabled={escT2.note.trim().length < 5 || (escT2.mode === "analyst" ? !escT2.assignee_id : !escT2.team_id)} className="rounded-sm font-mono text-xs uppercase">Escalate</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Dialog open={releaseOpen} onOpenChange={setReleaseOpen}>
                <DialogContent className="rounded-sm" data-testid="soc-release-dialog">
                  <DialogHeader><DialogTitle className="font-display">Release {c.number} to the unassigned queue?</DialogTitle></DialogHeader>
                  <p className="text-sm text-muted-foreground">The case will become unassigned so another analyst can take it. SOC leads will be notified.</p>
                  <Textarea data-testid="soc-release-reason" value={releaseReason} onChange={e => setReleaseReason(e.target.value)} placeholder="Reason (optional) — e.g. end of shift, needs Tier 2, out of my scope…" className="rounded-sm text-sm min-h-[70px]" />
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setReleaseOpen(false)} className="rounded-sm font-mono text-xs uppercase">Cancel</Button>
                    <Button data-testid="soc-release-confirm" onClick={releaseCase} className="rounded-sm font-mono text-xs uppercase">Release case</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </Field>
            <Field label="Team">
              <Select value={c.team_id || "none"} onValueChange={setTeam} disabled={!canEdit}>
                <SelectTrigger data-testid="soc-team-select" className="rounded-sm h-8 text-xs"><SelectValue placeholder="No team" /></SelectTrigger>
                <SelectContent><SelectItem value="none">No team</SelectItem>{teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div>
              <div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Tags</div>
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="soc-tags">
                {(c.tags || []).map(t => (
                  <span key={t} data-testid={`soc-tag-${t}`} className="flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 border border-border rounded-sm bg-accent/30">
                    {t}{canEdit && <button data-testid={`soc-tag-remove-${t}`} onClick={() => removeTag(t)} className="text-muted-foreground hover:text-rag-red"><X size={11} /></button>}
                  </span>
                ))}
                {(c.tags || []).length === 0 && <span className="text-xs text-muted-foreground">No tags</span>}
              </div>
              {canEdit && (
                <div className="flex gap-1.5">
                  <Input data-testid="soc-tag-input" value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} placeholder="Add tag…" className="rounded-sm h-8 text-xs" />
                  <Button data-testid="soc-tag-add" onClick={addTag} size="sm" variant="outline" className="rounded-sm h-8"><Plus size={13} /></Button>
                </div>
              )}
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Handover note</div>
              {canEdit ? (
                <div className="flex gap-1.5">
                  <Input data-testid="soc-handover-note" value={hnote} onChange={e => setHnote(e.target.value)}
                    onBlur={() => { if (hnote !== (c.handover_note || "")) patch({ handover_note: hnote }); }}
                    placeholder="What's pending for the next shift…" className="rounded-sm h-8 text-xs" />
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">{c.handover_note || "—"}</div>
              )}
            </div>
          </Card>
          <Card className="p-5 border-border rounded-sm space-y-2 text-xs font-mono" data-testid="soc-sla-card">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">SLA</div>
            <Row label="Response due" value={fmtIST(c.sla_response_due_at)} bad={c.response_breached} />
            <Row label="Resolution due" value={fmtIST(c.sla_resolution_due_at)} bad={c.resolution_breached} />
            {c.sla_profile_name && <Row label="SLA clock" value={c.sla_profile_name} />}
            <Row label="Responded" value={c.responded_at ? fmtIST(c.responded_at) : "pending"} />
            <Row label="Resolved" value={fmtIST(c.resolved_at)} />
          </Card>
          {links.length > 0 && (
            <Card className="p-5 border-border rounded-sm space-y-2" data-testid="soc-links-card">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-2"><ArrowBendUpRight size={13} /> Linked ITSM tickets</div>
              {links.map(l => (
                <div key={l.id} data-testid={`soc-link-${l.incident_number}`} className="border border-border rounded-sm px-3 py-2 bg-card/40">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-primary">{l.incident_number}</span>
                    <span className={`text-[10px] font-mono px-2 py-0.5 border ${priorityColor(l.incident_priority)}`}>{l.incident_priority}</span>
                    <span className="text-[10px] font-mono px-2 py-0.5 border border-border rounded-sm uppercase">{(l.incident_status || "").replace(/_/g, " ")}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 truncate">{l.incident_title}</div>
                  <div className="text-[10px] font-mono text-muted-foreground">by {l.created_by}{l.incident_assignee ? ` · assigned ${l.incident_assignee}` : ""}</div>
                </div>
              ))}
            </Card>
          )}
        </div>
      </div>

      <Dialog open={escOpen} onOpenChange={setEscOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="soc-escalate-dialog">
          <DialogHeader><DialogTitle className="font-mono uppercase text-sm">Escalate {c.number} to ITSM</DialogTitle></DialogHeader>
          <div className="p-3 rounded-sm border border-rag-amber/40 bg-rag-amber-bg text-xs text-rag-amber-fg mb-1">
            Only the text you enter below crosses to ITSM. SOC-sensitive data (IOCs, TLP, ATT&CK, evidence) is never shared with ITSM agents.
          </div>
          <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
            <div>
              <label className="text-xs font-mono text-muted-foreground">Ticket title</label>
              <Input data-testid="soc-esc-title" value={escForm.title} onChange={e => setEscForm({ ...escForm, title: e.target.value })} className="rounded-sm mt-1" />
            </div>
            <div>
              <label className="text-xs font-mono text-muted-foreground">Description (shared with ITSM)</label>
              <Textarea data-testid="soc-esc-desc" value={escForm.description} onChange={e => setEscForm({ ...escForm, description: e.target.value })} className="rounded-sm mt-1 min-h-[80px]" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-mono text-muted-foreground">Priority</label>
                <Select value={escForm.priority} onValueChange={v => setEscForm({ ...escForm, priority: v })}>
                  <SelectTrigger data-testid="soc-esc-priority" className="rounded-sm mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{["P1", "P2", "P3", "P4"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-mono text-muted-foreground">Type</label>
                <Select value={escForm.incident_type} onValueChange={v => setEscForm({ ...escForm, incident_type: v })}>
                  <SelectTrigger data-testid="soc-esc-type" className="rounded-sm mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{INCIDENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-mono text-muted-foreground">Client</label>
                <Select value={escForm.client} onValueChange={v => setEscForm({ ...escForm, client: v })}>
                  <SelectTrigger data-testid="soc-esc-client" className="rounded-sm mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Unassigned">Unassigned</SelectItem>{clients.map(cl => <SelectItem key={cl.id} value={cl.name}>{cl.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm text-xs font-mono uppercase" onClick={() => setEscOpen(false)}>Cancel</Button>
            <Button data-testid="soc-esc-confirm" className="rounded-sm text-xs font-mono uppercase" onClick={doEscalate}><ArrowSquareOut size={14} className="mr-1" /> Create ITSM ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="soc-bulk-dialog">
          <DialogHeader><DialogTitle className="font-mono uppercase text-sm">Bulk add observables</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Paste any text — IPs, domains, URLs, hashes, emails, filenames or usernames (separated by spaces, commas or new lines). Types are auto-detected. Review before committing.</p>
            <Textarea data-testid="soc-bulk-text" value={bulkText} onChange={e => setBulkText(e.target.value)} placeholder={"8.8.8.8\nevil.example.com\nhttps://bad.link/x\nd41d8cd98f00b204e9800998ecf8427e"} className="rounded-sm min-h-[120px] text-sm font-mono" />
            <Button data-testid="soc-bulk-parse" onClick={parseBulk} size="sm" variant="outline" className="rounded-sm font-mono uppercase text-xs">Parse</Button>
            {bulkPreview.length > 0 && (
              <div className="border border-border rounded-sm max-h-52 overflow-y-auto divide-y divide-border" data-testid="soc-bulk-preview">
                {bulkPreview.map((it, i) => (
                  <div key={i} className="flex items-center gap-2 px-2 py-1.5 text-xs">
                    <Select value={it.type} onValueChange={v => setBulkPreview(p => p.map((x, j) => j === i ? { ...x, type: v } : x))}>
                      <SelectTrigger className="h-7 w-28 rounded-sm text-[11px]"><SelectValue /></SelectTrigger>
                      <SelectContent>{IOC_TYPES.map(t => <SelectItem key={t} value={t}>{t.toUpperCase()}</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="font-mono truncate flex-1">{it.value}</span>
                    <button onClick={() => setBulkPreview(p => p.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-rag-red"><X size={13} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm text-xs font-mono uppercase" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button data-testid="soc-bulk-commit" disabled={!bulkPreview.length} className="rounded-sm text-xs font-mono uppercase" onClick={commitBulk}>Add {bulkPreview.length || ""} observable{bulkPreview.length === 1 ? "" : "s"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MitreGateDialog open={gateOpen} onClose={() => setGateOpen(false)} c={c} mitreAll={mitreAll} lead={isLead(user)} minLen={gate.min_reason_len}
        onPatch={async (body) => { const { data } = await api.patch(`/soc/cases/${id}`, body); setC(data); }} onProceed={gateProceed} />
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="rounded-sm" data-testid="soc-close-dialog">
          <DialogHeader><DialogTitle className="font-mono uppercase text-sm">Close case {c.number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-mono text-muted-foreground">Classification</label>
              <Select value={closeForm.classification} onValueChange={v => setCloseForm({ ...closeForm, classification: v })}>
                <SelectTrigger data-testid="soc-close-classification" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{SOC_CLASSIFICATIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-mono text-muted-foreground">Close reason</label>
              <Textarea data-testid="soc-close-reason" value={closeForm.close_reason} onChange={e => setCloseForm({ ...closeForm, close_reason: e.target.value })} className="rounded-sm mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm text-xs font-mono uppercase" onClick={() => setCloseOpen(false)}>Cancel</Button>
            <Button data-testid="soc-close-confirm" className="rounded-sm text-xs font-mono uppercase" onClick={doClose}>Close case</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MitreTacticsCard({ c, patch, canEdit }) {
  const [tactics, setTactics] = useState([]);
  useEffect(() => { api.get("/soc/mitre/tactics").then(r => setTactics(r.data)).catch(() => {}); }, []);
  const sel = c.mitre_tactics || [];
  const toggle = (t) => canEdit && patch({ mitre_tactics: sel.includes(t) ? sel.filter(x => x !== t) : [...sel, t] });
  return (
    <Card className="p-4 border-border rounded-sm" data-testid="soc-mitre-tactics-card">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Tactic-level tags <span className="normal-case tracking-normal">· use when the exact technique is unknown</span></div>
      <div className="flex flex-wrap gap-1.5">
        {tactics.map(t => <button key={t} type="button" data-testid={`soc-tactic-${t}`} onClick={() => toggle(t)} aria-pressed={sel.includes(t)} disabled={!canEdit}
          className={`text-[10px] font-mono uppercase px-2 py-1 rounded-sm border transition-colors ${sel.includes(t) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{t}</button>)}
      </div>
      {c.mitre_unmappable && <div className="mt-3 text-xs text-muted-foreground" data-testid="soc-mitre-unmappable-note">Marked <span className="font-medium text-foreground">not mappable</span>: {c.mitre_unmappable_reason}
        {canEdit && <button onClick={() => patch({ mitre_unmappable: false })} className="ml-2 text-primary hover:underline text-[11px]">clear</button>}</div>}
    </Card>
  );
}

const Field = ({ label, children }) => (
  <div><div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">{label}</div>{children}</div>
);
const Row = ({ label, value, bad }) => (
  <div className="flex items-center justify-between"><span className="text-muted-foreground">{label}</span><span className={bad ? "text-rag-red-fg" : ""}>{value}</span></div>
);
